import * as _ from 'lodash';
import * as React from 'react';
import * as ReactRouter from 'react-router';
import { diagnostics, utils } from '@blend/ui-common';
import { accessibilityUtils, colors, fonts, media } from '@blend/consumer-components';
import { DataShape, Field, FormProps, reduxForm } from 'redux-form';

import { css, StyleSheet } from 'aphrodite';
import * as queryString from 'query-string';

import TextScreenModalTrigger from 'login-components/Common/TextScreenModal/TextScreenModalTrigger';
import { formPost } from 'login-apis/formPost';
import * as privacyPolicyAPI from 'login-apis/privacyPolicyAPI';
import * as accountOpeningAPI from 'login-apis/accountOpeningAPI';
import * as accountOpeningUtils from 'login-utils/accountOpeningUtils';
import * as cookie from 'react-cookie';
import * as onboardingForm from 'login-utils/onboardingForm';
import { ProductCardConfig } from '../ProductCard/ProductCard';
import ProductCardGroup from '../ProductCardGroup/ProductCardGroup';

const { featureFlags } = utils;

const styles = StyleSheet.create({
  root: {
    width: 560,
    [media.mobileMedium]: {
      width: '100%',
    },
  },
  footer: {
    ...fonts.Roboto,
    color: colors.clayPlus3,
    display: 'inline',
    fontSize: 12,
  },
  footerLink: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
});

interface QueryProps {
  promoCode?: string;
}

interface AccountOpeningState {
  isApplyDisabled: boolean;
  products: accountOpeningAPI.Product[];
}

export class UnconnectedAccountOpening extends React.Component<
  ReactRouter.RouteComponentProps<{}, {}>,
  AccountOpeningState
> {
  state: AccountOpeningState = {
    isApplyDisabled: false,
    products: [],
  };

  async componentDidMount() {
    try {
      const { data: products } = await accountOpeningAPI.getProducts();
      this.setState({ products });
    } catch (err) {
      // istanbul ignore next
      diagnostics.error(err);
    }
  }

  // TODO: AO-101. Remove object-to-formdata dependency and use an actual form to post this
  onSubmit = async (referenceId: accountOpeningAPI.Product['referenceId']) => {
    const { products } = this.state;
    const product = _.find(products, { referenceId }) as accountOpeningAPI.Product;
    const { accounts } = product;
    this.setState({ isApplyDisabled: true });
    if (accountOpeningUtils.shouldUpdateAccountOpeningApp()) {
      await accountOpeningUtils.updateAccountOpeningApp(accounts, referenceId);
      return;
    }

    const _csrf = cookie.load('XSRF-TOKEN');
    const { location } = this.props;
    const { promoCode } = queryString.parse(location.search) as QueryProps;
    formPost('/api/public/signup/account-opening', {
      accounts,
      _csrf,
      promoCode,
      productReferenceId: referenceId,
    });
  };

  getProductCardConfigs(): ProductCardConfig[] {
    const { isApplyDisabled, products } = this.state;

    const productsToDisplay: accountOpeningAPI.Product[] = _.reject(products, { hidden: true });
    return _.map(productsToDisplay, product => ({
      value: product.referenceId,
      productId: product.referenceId,
      title: product.title,
      description: product.description,
      hasDetails: !!_.get(product, 'detailsDisplay.body'),
      isDisabled: isApplyDisabled,
      modalTitle: _.get(product, 'detailsDisplay.title') || product.title,
      modalContent: _.get(product, 'detailsDisplay.body'),
      modalNotes: _.get(product, 'detailsDisplay.footers') /* istanbul ignore next */ || [],
      imageLink: null,
    }));
  }

  render(): React.ReactNode {
    const { products } = this.state;

    // Wait until products are fetched to render inner content
    if (_.isEmpty(products)) return null;

    return (
      <div className={css(styles.root)}>
        <Field
          name={''}
          component={ProductCardGroup}
          configs={this.getProductCardConfigs()}
          onSelect={this.onSubmit}
          defaultSVG={null}
        />
        {!featureFlags.isEnabled('enableAccountOpeningPromoCode02042019') && (
          <p className={css(styles.footer)}>
            By clicking "Apply," I confirm that I have read, understood, and consent to the&nbsp;
            <TextScreenModalTrigger
              textScreenId={'terms'}
              headerText="Terms Of Use"
              renderTrigger={({ openModal }) => (
                // TODO (UI-5264) : Make trigger button a shared component since they share a lot of functionality
                <span
                  className={css(styles.footer, styles.footerLink)}
                  onClick={openModal}
                  onKeyDown={openModal && accessibilityUtils.generateOnKeyDown(openModal)}
                  tabIndex={0}
                >
                  Terms of Use
                </span>
              )}
            />
            ,&nbsp;
            <TextScreenModalTrigger
              textScreenId={privacyPolicyAPI.getPrivacyPolicyTextScreenId()}
              headerText="Privacy Policy"
              renderTrigger={({ openModal }) => (
                <span
                  className={css(styles.footer, styles.footerLink)}
                  onClick={openModal}
                  onKeyDown={openModal && accessibilityUtils.generateOnKeyDown(openModal)}
                  tabIndex={0}
                >
                  Privacy Policy
                </span>
              )}
            />
            , and&nbsp;
            <TextScreenModalTrigger
              textScreenId={'required-blend-e-consent'}
              headerText="ESIGN Act Consent"
              renderTrigger={({ openModal }) => (
                <span
                  className={css(styles.footer, styles.footerLink)}
                  onClick={openModal}
                  onKeyDown={openModal && accessibilityUtils.generateOnKeyDown(openModal)}
                  tabIndex={0}
                >
                  ESIGN Act Consent
                </span>
              )}
            />
            .
          </p>
        )}
      </div>
    );
  }
}

export default reduxForm<
  FormProps<DataShape, ReactRouter.RouteComponentProps<{}, {}>, ReduxState>,
  ReactRouter.RouteComponentProps<{}, {}>,
  ReduxState
>(onboardingForm.FORM_CONFIG)(UnconnectedAccountOpening);
